import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom"

import { PageNotFound } from "@costory/front/components/PageNotFound"
import {
  AuthGuard,
  AuthGuardWithoutOrganization,
} from "@costory/front/components/auth/AuthGuard"
import { AuthGuardWithoutOrganizationSetupDomain } from "@costory/front/components/auth/SetupDomain"
import { SignUp } from "@costory/front/components/auth/SignIn"
import { AppLayout } from "@costory/front/components/layout/AppLayout"
import { ForceColorScheme } from "@costory/front/components/layout/ForceColorScheme"
import { AlertCreationPage } from "@costory/front/pages/Alerts/AlertCreationPage"
import { AlertEditionPage } from "@costory/front/pages/Alerts/AlertEditionPage"
import { AlertsListPage } from "@costory/front/pages/Alerts/AlertListPage"
import { Analyze } from "@costory/front/pages/Analyze"
import { BudgetCreationPage } from "@costory/front/pages/Budgets/BudgetCreationPage"
import { BudgetEditionPage } from "@costory/front/pages/Budgets/BudgetEditionPage"
import { BudgetRevisionPage } from "@costory/front/pages/Budgets/BudgetRevisionPage"
import { BudgetPage } from "@costory/front/pages/Budgets/BudgetViewPage"
import { BudgetsListPage } from "@costory/front/pages/Budgets/BudgetsListPage"
import { DashboardEditionPage } from "@costory/front/pages/Dashboards/DashboardEditionPage"
import { BillingDatasourcesPage } from "@costory/front/pages/Datasources/BillingDatasourcesPage"
import { MetricsDatasourcesPage } from "@costory/front/pages/Datasources/MetricsDatasourcesPage"
import { EditDigest } from "@costory/front/pages/DigestV2/EditDigest"
import { ListPageWrapper } from "@costory/front/pages/DigestV2/ListPage"
import { NewDigest } from "@costory/front/pages/DigestV2/NewDigest"
import { SummaryPageWrapper } from "@costory/front/pages/DigestV2/SummaryPage"
import { OrganizationKnowledgePage } from "@costory/front/pages/DomainKnowledge"
import { EmbedPage } from "@costory/front/pages/EmbedExplorer"
import { FormulasPage } from "@costory/front/pages/Formulas"
import { InitiativePage } from "@costory/front/pages/Initiatives/InitiativePage"
import { InitiativeListPage } from "@costory/front/pages/Initiatives/InitiativesListPage"
import { IntegrationPage } from "@costory/front/pages/Integration"
import { SlackOAuthRedirect } from "@costory/front/pages/Integration/SlackOAuthRedirect"
import { LabelsExplorerPage } from "@costory/front/pages/LabelsExplorer"
import {
  OnboardingGuard,
  OnboardingPage,
} from "@costory/front/pages/Onboarding"
import { RegionSettingPage } from "@costory/front/pages/RegionSettings"
import { ReportsPage } from "@costory/front/pages/Reports/ReportsExplorerPage"
import { TagsPage } from "@costory/front/pages/Tags"
import { EditVirtualDimensionVersion } from "@costory/front/pages/VirtualDimensions/EditVirtualDimensionVersion"
import { JobExecutionsPage } from "@costory/front/pages/VirtualDimensions/JobExecutions"

import { DashboardCreationPage } from "./Dashboards/DashboardCreationPage"
import { DataHealthPage } from "./DataHealth"
import { DatasourcesPage as LegacyDatasourcesPage } from "./Datasources"
import { ExplorerPage } from "./Explorer"
import { HomePage } from "./Home/HomePage"
import { InsightsPage } from "./Insights"
import { SavedViewsPage } from "./SavedViews"
import { ScheduledDigestConfigurationsPage } from "./ScheduledDigestConfigurations"
import { VirtualDimensionsPage } from "./VirtualDimensions"
import { EditVirtualDimensionPage } from "./VirtualDimensions/EditVirtualDimension"
import { NewVirtualDimensionPage } from "./VirtualDimensions/NewVirtualDimension"

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="org-selection" element={<AuthGuardWithoutOrganization />} />
      <Route
        path="setup-domain"
        element={<AuthGuardWithoutOrganizationSetupDomain />}
      />
      <Route path="sign-up" element={<SignUp />} />
      <Route element={<AuthGuard />}>
        <Route path="embed">
          <Route path=":orgId/:chartType" element={<EmbedPage />} />
        </Route>
        <Route element={<AppLayout />}>
          <Route element={<OnboardingGuard />}>
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="dashboards">
              <Route path="new" element={<DashboardCreationPage />} />
              <Route path=":dashboardId" element={<DashboardEditionPage />} />
            </Route>
            <Route path="explorer">
              <Route index element={<ExplorerPage />} />
              <Route path="views/:viewId" element={<ExplorerPage />} />
            </Route>

            <Route path="insights" element={<InsightsPage />} />
            <Route path="initiatives">
              <Route index element={<InitiativeListPage />} />
              <Route path=":initiativeId" element={<InitiativePage />} />
            </Route>

            <Route path="data-health" element={<DataHealthPage />} />
            <Route path="digests">
              <Route index element={<ListPageWrapper />} />
              <Route
                path="new/:period/:datePreset/:selectedNodeId?"
                element={<NewDigest />}
              />
              <Route
                path="edit/:digestId/:selectedNodeId?"
                element={<EditDigest />}
              />
              <Route
                path="summary/:digestId/:limit?"
                element={<SummaryPageWrapper />}
              />
            </Route>
            <Route
              path="scheduled-reports"
              element={<ScheduledDigestConfigurationsPage />}
            />
            <Route path="tags" element={<TagsPage />} />
            <Route path="saved-views" element={<SavedViewsPage />} />
            <Route path="alerts">
              <Route index element={<AlertsListPage />} />
              <Route path="new" element={<AlertCreationPage />} />
              <Route path=":alertId/edit" element={<AlertEditionPage />} />
            </Route>
            <Route path="budgets" element={<ForceColorScheme />}>
              <Route index element={<BudgetsListPage />} />
              <Route path="new" element={<BudgetCreationPage />} />
              <Route path=":budgetId">
                <Route index element={<BudgetPage />} />
                <Route path="edit" element={<BudgetEditionPage />} />
                <Route path="new" element={<BudgetRevisionPage />} />
              </Route>
            </Route>
            <Route
              path="setup-executions"
              element={<JobExecutionsPage type="full_refresh" />}
            />

            <Route path="*" element={<PageNotFound />} />
          </Route>
          <Route path="formulas">
            <Route index element={<FormulasPage />} />
            <Route path="new/:datasourceId" element={<FormulasPage />} />
          </Route>
          <Route path="datasources">
            <Route index element={<Navigate to="legacy" />} />
            <Route path="billing" element={<BillingDatasourcesPage />} />
            <Route path="metrics" element={<MetricsDatasourcesPage />} />
            <Route path="legacy" element={<LegacyDatasourcesPage />} />
          </Route>
          <Route path="labels-explorer" element={<LabelsExplorerPage />} />
          <Route path="virtual-dimensions">
            <Route index element={<VirtualDimensionsPage />} />
            <Route
              path="job-executions"
              element={<JobExecutionsPage type="update" />}
            />
            <Route path="new" element={<NewVirtualDimensionPage />} />
            <Route
              path=":virtualDimensionId"
              element={<EditVirtualDimensionPage />}
            />
            <Route
              path=":virtualDimensionId/draft/:draftId"
              element={<EditVirtualDimensionVersion />}
            />
          </Route>
          <Route path="integration" element={<IntegrationPage />} />
          <Route path="slack/redirect" element={<SlackOAuthRedirect />} />
          <Route path="analyze" element={<Analyze />} />
          <Route path="region-settings" element={<RegionSettingPage />} />
          <Route
            path="organization-knowledge"
            element={<OrganizationKnowledgePage />}
          />
        </Route>
      </Route>
    </Route>,
  ),
)
